// Till payments (Phase 11, ADR 0037): the M-Pesa number businesses may send
// payment prompts to. The individual nominates their registered phone, or
// another number confirmed by an SMS code, after a step-up; turning prompts
// off needs nothing. Businesses never see the number. The recent requests
// list shows which business asked for how much, and whether it was paid.
import { Badge, Button, Group, Paper, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useStepUp } from "../../shared/session/stepup";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { TILL_STATUS_COLOUR } from "../../shared/ui/tillPayments";

type Mpesa = Schemas["MpesaNumber"];

const KEY = ["me", "mpesa-number"] as const;
const PAYMENTS_KEY = ["me", "till-payments"] as const;

export function MpesaSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  const number = useQuery({ queryKey: KEY, queryFn: () => unwrap(api.GET("/me/mpesa-number")) });
  const [other, setOther] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const n = number.data;

  const saved = (m: Mpesa) => {
    queryClient.setQueryData(KEY, m);
    if (m.nominated) setNotice(t("mpesa.saved", { phone: m.masked_phone ?? "" }));
  };

  const nominate = async (phone?: string) => {
    setError(null);
    setNotice(null);
    const token = await stepUp("mpesa_number");
    if (!token) return;
    setBusy(phone ? "other" : "registered");
    const { data, error } = await api.PUT("/me/mpesa-number", {
      params: { header: { "X-Step-Up-Token": token } },
      body: phone ? { phone } : {},
    });
    setBusy(null);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setOther(null);
    setOtp("");
    saved(data.number);
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy("confirm");
    const { data, error } = await api.POST("/me/mpesa-number/verify", { body: { otp: otp.trim() } });
    setBusy(null);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setOtp("");
    saved(data);
  };

  const turnOff = async () => {
    setError(null);
    setNotice(null);
    setBusy("off");
    const { response, error } = await api.DELETE("/me/mpesa-number");
    setBusy(null);
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    queryClient.setQueryData(KEY, { nominated: false, same_as_phone: false });
    setNotice(t("mpesa.removed"));
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="mpesa-section">
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={2} size="h4">
            {t("mpesa.title")}
          </Title>
          {n && (
            <Badge color={n.nominated ? "green" : "gray"} data-testid="mpesa-status">
              {n.nominated ? t("mpesa.status_on") : t("mpesa.status_off")}
            </Badge>
          )}
        </Group>
        <Text size="sm">{t("mpesa.intro")}</Text>
        {n && (
          <Text size="sm" fw={500} data-testid="mpesa-current">
            {n.nominated
              ? n.same_as_phone
                ? t("mpesa.on_registered", { phone: n.masked_phone ?? "" })
                : t("mpesa.on", { phone: n.masked_phone ?? "" })
              : t("mpesa.off")}
          </Text>
        )}
        {notice && <Notice>{notice}</Notice>}
        <FormError message={error} />
        {n?.pending_masked_phone && (
          <form onSubmit={(e) => void confirm(e)}>
            <Stack gap="xs">
              <Text size="sm">{t("mpesa.pending", { phone: n.pending_masked_phone })}</Text>
              <Group align="flex-end">
                <TextInput
                  label={t("mpesa.code_label")}
                  value={otp}
                  onChange={(e) => setOtp(e.currentTarget.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={12}
                  required
                  w={180}
                />
                <Button type="submit" loading={busy === "confirm"}>
                  {t("mpesa.confirm")}
                </Button>
              </Group>
            </Stack>
          </form>
        )}
        {other !== null ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void nominate(other.trim());
            }}
          >
            <Group align="flex-end">
              <TextInput
                label={t("mpesa.other_label")}
                description={t("mpesa.other_hint")}
                value={other}
                onChange={(e) => setOther(e.currentTarget.value)}
                inputMode="tel"
                autoComplete="tel"
                maxLength={20}
                required
                w={260}
              />
              <Button type="submit" loading={busy === "other"}>
                {t("mpesa.send_code")}
              </Button>
              <Button variant="subtle" onClick={() => setOther(null)}>
                {t("mpesa.cancel")}
              </Button>
            </Group>
          </form>
        ) : (
          <Group gap="sm">
            {!(n?.nominated && n.same_as_phone) && (
              <Button variant="light" loading={busy === "registered"} onClick={() => void nominate()}>
                {t("mpesa.use_registered")}
              </Button>
            )}
            <Button variant="default" onClick={() => setOther("")}>
              {t("mpesa.use_other")}
            </Button>
            {n?.nominated && (
              <Button variant="subtle" color="red" loading={busy === "off"} onClick={() => void turnOff()}>
                {t("mpesa.turn_off")}
              </Button>
            )}
          </Group>
        )}
        <RecentRequests />
      </Stack>
    </Paper>
  );
}

function RecentRequests() {
  const { t } = useTranslation();
  const f = useFormat();
  const payments = useQuery({ queryKey: PAYMENTS_KEY, queryFn: () => unwrap(api.GET("/me/till-payments")) });
  const items = payments.data?.items ?? [];
  return (
    <Stack gap="xs" mt="xs">
      <Title order={3} size="h5">
        {t("mpesa.recent_title")}
      </Title>
      {items.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="till-requests-empty">
          {t("mpesa.recent_empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={480}>
          <Table aria-label={t("mpesa.recent_title")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("mpesa.business")}</Table.Th>
                <Table.Th>{t("mpesa.amount")}</Table.Th>
                <Table.Th>{t("mpesa.status")}</Table.Th>
                <Table.Th>{t("mpesa.when")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((p) => (
                <Table.Tr key={p.payment_id} data-testid="till-request-row">
                  <Table.Td>{p.company_name ?? ""}</Table.Td>
                  <Table.Td>{f.money(p.amount * 100, p.currency)}</Table.Td>
                  <Table.Td>
                    <Badge color={TILL_STATUS_COLOUR[p.status]}>{t(`till_status.${p.status}`)}</Badge>
                    {p.receipt && (
                      <Text size="xs" c="dimmed">
                        {t("mpesa.receipt")}: {p.receipt}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{f.dateTime(p.created_at)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
